import type { FeatureMap } from './specimenFeatures';
import type { Domain, FiveKingdom } from './taxonomyDatabase';

export interface KingdomInference {
  fiveKingdom: FiveKingdom | null;
  domain: Domain | null;
  confidence: number;
  alternatives: FiveKingdom[];
  reasoning: string[];
  needsMoreEvidence: boolean;
}

const emptyInference = (): KingdomInference => ({ fiveKingdom: null, domain: null, confidence: 0, alternatives: [], reasoning: ['Record observable cellular and nutritional evidence before assigning a kingdom.'], needsMoreEvidence: true });

/** Infers a curriculum kingdom from observed traits; incomplete evidence remains explicitly uncertain. */
export function inferKingdom(features: FeatureMap): KingdomInference {
  const scores: Record<FiveKingdom, number> = { Monera: 0, Protista: 0, Fungi: 0, Plantae: 0, Animalia: 0 };
  const reasoning: string[] = [];
  const cellType = features.cellType;
  const organization = features.cellOrganization;
  const nutrition = features.nutrition;

  if (cellType === 'prokaryotic') { scores.Monera += 5; reasoning.push('Prokaryotic cell structure supports traditional kingdom Monera.'); }
  if (cellType === 'eukaryotic') { scores.Protista += 1; scores.Fungi += 1; scores.Plantae += 1; scores.Animalia += 1; }
  if (organization === 'unicellular') scores.Protista += 3;
  if (organization === 'colonial-filament' && cellType === 'prokaryotic') scores.Monera += 2;
  if (String(organization).includes('multicellular')) { scores.Fungi += 1; scores.Plantae += 1; scores.Animalia += 1; }
  if (nutrition === 'photosynthetic') { scores.Plantae += 3; if (organization === 'unicellular') scores.Protista += 1; }
  if (nutrition === 'mixotrophic') scores.Protista += 3;
  if (nutrition === 'heterotrophic-ingestion' && organization !== 'unicellular') scores.Animalia += 3;
  if (nutrition === 'saprotrophic-absorption') scores.Fungi += 4;
  if (features.cellWallMaterial === 'chitin') scores.Fungi += 4;
  if (features.cellWallMaterial === 'cellulose') scores.Plantae += 2;
  if (features.cellWall === false && nutrition === 'heterotrophic-ingestion') scores.Animalia += 2;
  if (features.chlorophyll === true && features.cellType === 'prokaryotic') scores.Monera += 2;

  const sorted = (Object.entries(scores) as Array<[FiveKingdom, number]>).sort((a, b) => b[1] - a[1]);
  const [winner, topScore] = sorted[0];
  const secondScore = sorted[1]?.[1] || 0;
  if (topScore === 0 || (topScore === secondScore && topScore < 3)) return { ...emptyInference(), reasoning: ['The observed traits do not yet distinguish a kingdom. Record more evidence.'] };

  let domain: Domain | null = null;
  if (winner === 'Monera') {
    if (features.domainClue === 'extreme-saline' || features.domain === 'Archaea' || features.peptidoglycan === false) domain = 'Archaea';
    else if (features.domain === 'Bacteria' || features.peptidoglycan === true || features.chlorophyll === true) domain = 'Bacteria';
    if (domain) reasoning.push(`The three-domain mapping places this Monera example in ${domain}.`);
  } else {
    domain = 'Eukarya';
  }

  const total = Object.values(scores).reduce((sum, score) => sum + score, 0);
  const confidence = total ? Math.round((topScore / total) * 100) : 0;
  const alternatives = sorted.filter(([, score]) => score > 0 && score >= topScore - 1).map(([kingdom]) => kingdom);
  if (winner === 'Plantae') reasoning.push('Photosynthesis and cellulose cell walls support the plant classification; use vascular tissue and reproductive structures to identify the division.');
  if (winner === 'Fungi') reasoning.push('Absorptive heterotrophy and/or chitin cell walls distinguish fungi from plants.');
  if (winner === 'Animalia') reasoning.push('Ingestive heterotrophy and absence of cell walls support animal classification.');
  if (winner === 'Protista') reasoning.push('A eukaryotic organism with a simple or unicellular organization is consistent with the traditional protist grouping.');
  return { fiveKingdom: winner, domain, confidence, alternatives, reasoning, needsMoreEvidence: confidence < 70 || alternatives.length > 1 };
}
